import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { transformGlobalContent } from '@confluence/page-card/entry-points/transformers.ts';

import type { EndOfPageRecommendationTwgQuery$data } from './__generated__/EndOfPageRecommendationTwgQuery.graphql.ts';

// Strategy label for the TWG arm (TWG has no `strategy`), for analytics parity.
export const TWG_STRATEGY = 'TWG';

// Inline-hydrated content node. The graph relationship returns both pages and blog
// posts, so `data` selects `... on ConfluencePage` and `... on ConfluenceBlogPost`.
// Relay types a multi-fragment union field awkwardly for direct field access, so we
// read it through this hand-typed shape (a superset of both fragments' selections).
// `pageId` is present for pages, `blogPostId` for blog posts.
type TwgContentNode = {
	pageId?: string | null;
	blogPostId?: string | null;
	title?: string | null;
	subtype?: string | null;
	// `status` is aliased per type in the query (ConfluencePage.status and
	// ConfluenceBlogPost.status are different enum types); read whichever is present.
	pageStatus?: string | null;
	blogPostStatus?: string | null;
	links?: { webUi?: string | null } | null;
	space?: {
		id?: string | null;
		key?: string | null;
		alias?: string | null;
		name?: string | null;
	} | null;
	owner?: { user?: { name?: string | null; accountId?: string | null } | null } | null;
	properties?: ReadonlyArray<{ key: string; value: string }> | null;
};

const contentIdOf = (node: TwgContentNode): string | undefined =>
	node.pageId ?? node.blogPostId ?? undefined;

// Content statuses that cc-smarts would never recommend. We filter these out of the
// TWG results so treatment doesn't surface drafts/archived/trashed/etc. that control
// can't (the graph traversal isn't status-filtered, unlike the cc-smarts backend).
const NON_CURRENT_STATUSES = new Set(['archived', 'deleted', 'draft', 'historical', 'trashed']);

// Keep CURRENT and unknown/absent status (ConfluenceBlogPost.status is nullable and may
// not hydrate), dropping only definitively non-current content. Lenient on purpose so a
// blog whose status doesn't resolve isn't silently dropped.
const isCurrentContent = (node: TwgContentNode): boolean => {
	const status = node.pageStatus ?? node.blogPostStatus;
	return !status || !NON_CURRENT_STATUSES.has(String(status).toLowerCase());
};

/**
 * Ordered, de-duplicated hydrated content nodes (pages + blog posts) from a
 * `graphStore_cypherQueryV2` result. Order = graph traversal (TWG ranking).
 * Non-current content (draft/archived/trashed/…) is filtered out for parity with
 * the cc-smarts control arm.
 */
export const extractTwgRecContent = (
	data: EndOfPageRecommendationTwgQuery$data | null | undefined,
): TwgContentNode[] => {
	const seen = new Set<string>();
	const nodes: TwgContentNode[] = [];

	// Linear (bounded by the query's `first`); the perf heuristic is a false positive.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- bounded by the query's `first`
	const columns = (data?.graphStore_cypherQueryV2?.edges ?? []).flatMap(
		(edge) => edge?.node?.columns ?? [],
	);

	for (const column of columns) {
		// Relay's multi-fragment union type doesn't allow direct field access; read the
		// selected fields through TwgContentNode (which matches the query selection).
		const node = column?.value?.data as TwgContentNode | null | undefined;
		const contentId = node ? contentIdOf(node) : undefined;
		// Only ConfluencePage/ConfluenceBlogPost are selected, so a present content id is
		// the presence + identity check; other node types resolve to `{}` and are skipped.
		// Non-current content is dropped so treatment matches what cc-smarts would show.
		if (node && contentId && isCurrentContent(node) && !seen.has(contentId)) {
			seen.add(contentId);
			nodes.push(node);
		}
	}

	return nodes;
};

// Map an AGG content node onto the `content` shape transformGlobalContent expects.
// Renames: webUi->webui, subtype->subType, owner.user.name->displayName,
// owner.user.accountId->creatorId, properties->emoji.nodes. `type` is derived from
// which id is present (the AGG enum is `PAGE`/`BLOG_POST`, which wouldn't match the
// card's lower-case, no-underscore content types and would render the wrong icon).
const toGlobalContentShape = (node: TwgContentNode) => {
	const status = node.pageStatus ?? node.blogPostStatus;
	return {
		id: contentIdOf(node) ?? '',
		title: node.title ?? '',
		type: node.blogPostId ? 'blogpost' : 'page',
		subType: node.subtype ?? undefined,
		status: status ? String(status).toLowerCase() : undefined,
		links: {
			webui: node.links?.webUi ?? '',
		},
		space: {
			id: node.space?.id ?? '',
			key: node.space?.key ?? '',
			alias: node.space?.alias ?? '',
			name: node.space?.name ?? '',
		},
		creatorId: node.owner?.user?.accountId ?? '',
		history: {
			ownedBy: {
				displayName: node.owner?.user?.name ?? '',
			},
		},
		emoji: {
			nodes: node.properties ?? [],
		},
	};
};

/**
 * Adapts a TWG result into the `GlobalPageCard[]` shape the shared display
 * component consumes, preserving TWG rank order and matching the cc-smarts arm.
 */
export const transformTwgRecs = (
	data: EndOfPageRecommendationTwgQuery$data | null | undefined,
): GlobalPageCard[] => {
	return extractTwgRecContent(data).map((node) => {
		const card = transformGlobalContent(toGlobalContentShape(node));
		// Parity with the cc-smarts transformer: zero these + stamp a strategy.
		card.likesCount = 0;
		card.commentsCount = 0;
		card.recommendationStrategy = TWG_STRATEGY;
		return card;
	});
};
